import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiBox, FiDownload, FiPrinter, FiSearch, FiHash,
  FiDollarSign, FiBarChart2, FiTrendingUp, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import ItemSelect from '../../components/ItemSelect';
import CustomerSelect from '../../components/CustomerSelect';
import MultiCheckSelect from '../../components/MultiCheckSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const ItemLink = styled.span`
  cursor: pointer;
  color: #1a73e8;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

// ── Component ──

const ItemSaleAnalysis = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Several categories can be ticked (2026-10-12). Empty = All Categories.
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [taxFilter, setTaxFilter] = useState('both');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState('all');
  const [selectedAreaId, setSelectedAreaId] = useState('all');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortField, setSortField] = useState('totalAmount');
  const [sortDirection, setSortDirection] = useState('desc');

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // ── Load Categories ──
  const loadCategories = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'inventoryCategories');
      const q = query(ref, orderBy('categoryName', 'asc'));
      const snapshot = await getDocs(q);
      setCategories(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.isDeleted !== true));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [activeCompany]);

  // ── Load SubCategories ──
  const loadSubCategories = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'inventorySubCategories');
      const q = query(ref, orderBy('subCategoryName', 'asc'));
      const snapshot = await getDocs(q);
      setSubCategories(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isDeleted !== true));
    } catch (error) {
      console.error('Error loading sub-categories:', error);
    }
  }, [activeCompany]);

  // ── Load Inventory Items ──
  const loadInventoryItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'inventoryItems');
      const q = query(ref, orderBy('itemName', 'asc'));
      const snapshot = await getDocs(q);
      setInventoryItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));
    } catch (error) {
      console.error('Error loading inventory items:', error);
    }
  }, [activeCompany]);

  // ── Load Customers ──
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(ref, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // Load regions
  const loadRegions = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'regions');
      const snapshot = await getDocs(ref);
      setRegions(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading regions:', error);
    }
  }, [activeCompany]);

  // Load areas
  const loadAreas = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'areas');
      const snapshot = await getDocs(ref);
      setAreas(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  }, [activeCompany]);

  // ── Initialize ──
  useEffect(() => {
    loadFinancialYears();
    loadCategories();
    loadSubCategories();
    loadInventoryItems();
    loadCustomers();
    loadRegions();
    loadAreas();

    // Default date range: current month
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, [loadFinancialYears, loadCategories, loadSubCategories, loadInventoryItems, loadCustomers, loadRegions, loadAreas]);

  // ── Filtered SubCategories based on selected Category ──
  const selectedCategorySet = useMemo(() => new Set(selectedCategories), [selectedCategories]);
  const categoryOptions = useMemo(() => categories.map(cat => ({
    value: cat.id,
    label: `${cat.categoryCode ? `${cat.categoryCode} - ` : ''}${cat.categoryName || ''}`,
    shortLabel: cat.categoryName || cat.categoryCode || ''
  })), [categories]);
  // Names of the ticked categories, for the print / Excel heading ('' = all).
  const selectedCategoryNames = useMemo(() => categoryOptions
    .filter(o => selectedCategorySet.has(o.value)).map(o => o.shortLabel).join(', '), [categoryOptions, selectedCategorySet]);
  const filteredSubCategories = useMemo(() => {
    if (!selectedCategories.length) return subCategories;
    return subCategories.filter(sc => selectedCategorySet.has(sc.categoryId));
  }, [subCategories, selectedCategories, selectedCategorySet]);

  // ── Filtered Items dropdown based on selected Category/SubCategory ──
  const filteredItemsDropdown = useMemo(() => {
    let items = [...inventoryItems];
    if (selectedCategories.length) {
      items = items.filter(item => selectedCategorySet.has(item.categoryId));
    }
    if (selectedSubCategory) {
      items = items.filter(item => item.subCategoryId === selectedSubCategory);
    }
    return items;
  }, [inventoryItems, selectedCategories, selectedCategorySet, selectedSubCategory]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // ── Load Transactions ──
  const loadTransactions = async () => {
    if (!activeCompany?.id || !selectedFYId) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const conditions = [
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS', 'SR', 'NSR']),
        where('financialYearId', '==', selectedFYId)
      ];

      const q = query(transRef, ...conditions);
      const snapshot = await getDocs(q);

      let allTransactions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Filter only Posted/Paid/Partial transactions for analysis
      allTransactions = allTransactions.filter(t => ['Posted', 'Paid', 'Partial'].includes(t.status));

      // Date range filter
      if (dateFrom) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') <= dateTo);
      }

      // Customer filter
      if (selectedCustomer) {
        allTransactions = allTransactions.filter(t => t.partyId === selectedCustomer);
      }

      // Region filter
      if (selectedRegionId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.regionId === selectedRegionId;
        });
      }

      // Area filter
      if (selectedAreaId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.areaId === selectedAreaId;
        });
      }

      // Status filter (additional beyond Posted)
      if (selectedStatus !== 'All') {
        allTransactions = allTransactions.filter(t => t.status === selectedStatus);
      }

      // Tax filter
      if (taxFilter === 'tax') {
        allTransactions = allTransactions.filter(t => !['NSI', 'NSR'].includes(t.tranTypeCode));
      } else if (taxFilter === 'nonTax') {
        allTransactions = allTransactions.filter(t => !['SI', 'SR'].includes(t.tranTypeCode));
      }

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Build item-level aggregated data from transaction lines ──
  const itemData = useMemo(() => {
    if (transactions.length === 0) return [];

    // Build a lookup for inventory items
    const itemLookup = {};
    inventoryItems.forEach(item => {
      itemLookup[item.id] = item;
    });

    // Build category lookup
    const categoryLookup = {};
    categories.forEach(cat => {
      categoryLookup[cat.id] = cat.categoryName || cat.categoryCode || 'Unknown';
    });

    // Aggregate by itemId from lines arrays
    const itemMap = {};

    transactions.forEach(t => {
      const lines = t.lines || [];
      const isReturn = ['SR', 'NSR'].includes(t.tranTypeCode);

      lines.forEach(line => {
        const itemId = line.itemId || 'unknown';
        if (!itemMap[itemId]) {
          const masterItem = itemLookup[itemId];
          const catId = masterItem?.categoryId || line.categoryId || '';
          const catName = categoryLookup[catId] || '';

          itemMap[itemId] = {
            itemId,
            itemCode: line.itemCode || masterItem?.itemCode || '-',
            itemName: line.itemName || masterItem?.itemName || 'Unknown Item',
            categoryId: catId,
            categoryName: catName,
            unitOfMeasure: masterItem?.unitOfMeasure || line.unitOfMeasure || '-',
            qtySold: 0,
            qtyReturned: 0,
            totalAmount: 0,
            totalDiscount: 0,
            totalGST: 0,
            lineCount: 0
          };
        }

        const qty = Number(line.quantity) || 0;
        const amount = Number(line.lineTotal) || Number(line.amount) || 0;
        const discount = Number(line.discountAmount) || 0;
        const gst = Number(line.taxAmount) || 0;

        if (isReturn) {
          itemMap[itemId].qtyReturned += qty;
          itemMap[itemId].totalAmount -= amount;
          itemMap[itemId].totalDiscount -= discount;
          itemMap[itemId].totalGST -= gst;
        } else {
          itemMap[itemId].qtySold += qty;
          itemMap[itemId].totalAmount += amount;
          itemMap[itemId].totalDiscount += discount;
          itemMap[itemId].totalGST += gst;
        }

        itemMap[itemId].lineCount += 1;
      });
    });

    // Convert to array and compute derived values
    let items = Object.values(itemMap).map(item => {
      const netQty = item.qtySold - item.qtyReturned;
      const avgPrice = netQty > 0 ? item.totalAmount / netQty : 0;

      return {
        ...item,
        netQty,
        avgPrice
      };
    });

    // Apply category filter (any of the ticked categories)
    if (selectedCategories.length) {
      items = items.filter(item => selectedCategorySet.has(item.categoryId));
    }

    // Apply subcategory filter
    if (selectedSubCategory) {
      const masterItemsInSubCat = inventoryItems
        .filter(mi => mi.subCategoryId === selectedSubCategory)
        .map(mi => mi.id);
      items = items.filter(item => masterItemsInSubCat.includes(item.itemId));
    }

    // Apply item filter
    if (selectedItem) {
      items = items.filter(item => item.itemId === selectedItem);
    }

    return items;
  }, [transactions, inventoryItems, categories, selectedCategories, selectedCategorySet, selectedSubCategory, selectedItem]);

  // ── Compute % of total for each item ──
  const grandTotal = useMemo(() => {
    return itemData.reduce((sum, item) => sum + item.totalAmount, 0);
  }, [itemData]);

  const itemDataWithPercent = useMemo(() => {
    return itemData.map(item => ({
      ...item,
      percentOfTotal: grandTotal > 0 ? (item.totalAmount / grandTotal) * 100 : 0
    }));
  }, [itemData, grandTotal]);

  // ── Sorted Data ──
  const sortedData = useMemo(() => {
    const data = [...itemDataWithPercent];
    data.sort((a, b) => {
      let valA, valB;

      switch (sortField) {
        case 'itemCode':
          valA = (a.itemCode || '').toLowerCase();
          valB = (b.itemCode || '').toLowerCase();
          break;
        case 'itemName':
          valA = (a.itemName || '').toLowerCase();
          valB = (b.itemName || '').toLowerCase();
          break;
        case 'categoryName':
          valA = (a.categoryName || '').toLowerCase();
          valB = (b.categoryName || '').toLowerCase();
          break;
        case 'unitOfMeasure':
          valA = (a.unitOfMeasure || '').toLowerCase();
          valB = (b.unitOfMeasure || '').toLowerCase();
          break;
        case 'qtySold':
          valA = a.qtySold || 0;
          valB = b.qtySold || 0;
          break;
        case 'qtyReturned':
          valA = a.qtyReturned || 0;
          valB = b.qtyReturned || 0;
          break;
        case 'netQty':
          valA = a.netQty || 0;
          valB = b.netQty || 0;
          break;
        case 'avgPrice':
          valA = a.avgPrice || 0;
          valB = b.avgPrice || 0;
          break;
        case 'totalAmount':
          valA = a.totalAmount || 0;
          valB = b.totalAmount || 0;
          break;
        case 'percentOfTotal':
          valA = a.percentOfTotal || 0;
          valB = b.percentOfTotal || 0;
          break;
        default:
          valA = a.totalAmount || 0;
          valB = b.totalAmount || 0;
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [itemDataWithPercent, sortField, sortDirection]);

  // ── Summary Cards ──
  const summary = useMemo(() => {
    const totalItemsSold = itemData.length;
    const totalQtySold = itemData.reduce((sum, item) => sum + item.qtySold, 0);
    const totalSaleAmount = itemData.reduce((sum, item) => sum + item.totalAmount, 0);
    const totalNetQty = itemData.reduce((sum, item) => sum + item.netQty, 0);
    const avgUnitPrice = totalNetQty > 0 ? totalSaleAmount / totalNetQty : 0;

    return { totalItemsSold, totalQtySold, totalSaleAmount, avgUnitPrice };
  }, [itemData]);

  // ── Chart: Top 15 Items by Sale Amount (Bar Chart) ──
  const barChartData = useMemo(() => {
    if (itemDataWithPercent.length === 0) return [];

    return [...itemDataWithPercent]
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 15)
      .map(item => ({
        name: item.itemName.length > 20 ? item.itemName.substring(0, 20) + '...' : item.itemName,
        fullName: item.itemName,
        amount: item.totalAmount
      }));
  }, [itemDataWithPercent]);

  // ── Chart: Sales by Category (Pie Chart) ──
  const pieChartData = useMemo(() => {
    if (itemDataWithPercent.length === 0) return [];

    const categoryMap = {};
    itemDataWithPercent.forEach(item => {
      const catName = item.categoryName || 'Uncategorized';
      if (!categoryMap[catName]) {
        categoryMap[catName] = { name: catName, value: 0 };
      }
      categoryMap[catName].value += item.totalAmount;
    });

    const sorted = Object.values(categoryMap).sort((a, b) => b.value - a.value);

    // If more than 6 categories, group the rest as "Others"
    if (sorted.length <= 6) return sorted;

    const top5 = sorted.slice(0, 5);
    const othersTotal = sorted.slice(5).reduce((sum, c) => sum + c.value, 0);
    return [...top5, { name: 'Others', value: othersTotal }];
  }, [itemDataWithPercent]);

  // ── Table Totals ──
  const tableTotals = useMemo(() => {
    return sortedData.reduce((totals, item) => {
      totals.qtySold += item.qtySold;
      totals.qtyReturned += item.qtyReturned;
      totals.netQty += item.netQty;
      totals.totalAmount += item.totalAmount;
      return totals;
    }, { qtySold: 0, qtyReturned: 0, netQty: 0, totalAmount: 0 });
  }, [sortedData]);

  // ── Sort Handler ──
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <FiArrowUp size={11} style={{ marginLeft: 4 }} />
      : <FiArrowDown size={11} style={{ marginLeft: 4 }} />;
  };

  // ── CSV Export ──
  const handleExport = () => {
    if (sortedData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Item Sale Analysis Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'}\n`;
    if (selectedCategoryNames) csv += `"Categories: ${selectedCategoryNames.replace(/"/g, '""')}"\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;

    csv += `Total Items Sold,${summary.totalItemsSold}\n`;
    csv += `Total Qty Sold,${summary.totalQtySold}\n`;
    csv += `Total Sale Amount,${summary.totalSaleAmount}\n`;
    csv += `Avg Unit Price,${summary.avgUnitPrice}\n\n`;

    csv += 'Item Code,Item Name,Category,Unit,Qty Sold,Qty Returned,Net Qty,Avg Price,Total Amount,% of Total\n';

    sortedData.forEach(item => {
      csv += `"${item.itemCode}",`;
      csv += `"${(item.itemName || '').replace(/"/g, '""')}",`;
      csv += `"${(item.categoryName || '').replace(/"/g, '""')}",`;
      csv += `"${item.unitOfMeasure}",`;
      csv += `${item.qtySold},`;
      csv += `${item.qtyReturned},`;
      csv += `${item.netQty},`;
      csv += `${item.avgPrice.toFixed(2)},`;
      csv += `${item.totalAmount.toFixed(2)},`;
      csv += `${item.percentOfTotal.toFixed(1)}%\n`;
    });

    csv += `\nTotals,,,,"${tableTotals.qtySold}","${tableTotals.qtyReturned}","${tableTotals.netQty}",,"${tableTotals.totalAmount.toFixed(2)}",100%\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Item_Sale_Analysis_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    if (sortedData.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Item Sale Analysis</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:140px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Item Sale Analysis</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${dateFrom || 'Start'} to ${dateTo || 'End'} |
        Items: ${sortedData.length}
        ${selectedCategoryNames ? `<br/>Categories: ${selectedCategoryNames.replace(/</g, '&lt;')}` : ''}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Total Items Sold</div><div class="summary-val">${formatNumber(summary.totalItemsSold)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Qty Sold</div><div class="summary-val">${formatNumber(summary.totalQtySold)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Sale Amount</div><div class="summary-val">${formatAmountLocal(summary.totalSaleAmount)}</div></div>
        <div class="summary-box"><div class="summary-label">Avg Unit Price</div><div class="summary-val">${formatAmountLocal(summary.avgUnitPrice)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Item Code</th><th>Item Name</th><th>Category</th><th>Unit</th>
          <th class="right">Qty Sold</th><th class="right">Qty Returned</th><th class="right">Net Qty</th>
          <th class="right">Avg Price</th><th class="right">Total Amount</th><th class="right">% of Total</th>
        </tr></thead>
        <tbody>
          ${sortedData.map(item => `
            <tr>
              <td>${item.itemCode}</td>
              <td>${item.itemName}</td>
              <td>${item.categoryName || '-'}</td>
              <td>${item.unitOfMeasure}</td>
              <td class="right">${formatNumber(item.qtySold)}</td>
              <td class="right">${formatNumber(item.qtyReturned)}</td>
              <td class="right">${formatNumber(item.netQty)}</td>
              <td class="right">${formatAmountLocal(item.avgPrice)}</td>
              <td class="right">${formatAmountLocal(item.totalAmount)}</td>
              <td class="right">${item.percentOfTotal.toFixed(1)}%</td>
            </tr>
          `).join('')}
          <tr class="totals-row">
            <td colspan="4" style="text-align:right">Totals (${sortedData.length} items)</td>
            <td class="right">${formatNumber(tableTotals.qtySold)}</td>
            <td class="right">${formatNumber(tableTotals.qtyReturned)}</td>
            <td class="right">${formatNumber(tableTotals.netQty)}</td>
            <td class="right"></td>
            <td class="right">${formatAmountLocal(tableTotals.totalAmount)}</td>
            <td class="right">100%</td>
          </tr>
        </tbody>
      </table>
      <div style="margin-top:30px;text-align:center;color:#999;font-size:11px">
        Generated on ${new Date().toLocaleString()} | AMAL ERP
      </div>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Custom Chart Tooltips ──
  const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{data.payload.fullName || label}</ChartTooltipLabel>
        <ChartTooltipRow>
          <ChartTooltipDot style={{ background: data.color }} />
          <span>Sale Amount:</span>
          <strong>{formatCurrency(data.value)}</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  const PieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    const total = pieChartData.reduce((s, d) => s + d.value, 0);
    const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{data.name}</ChartTooltipLabel>
        <ChartTooltipRow>
          <span>Amount:</span>
          <strong>{formatCurrency(data.value)}</strong>
        </ChartTooltipRow>
        <ChartTooltipRow>
          <span>Share:</span>
          <strong>{pct}%</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  // ── Render ──
  return (
    <PageLayout pageTitle="Item Sale Analysis" breadcrumb="Reports / Sale Reports / Item Sale Analysis">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Category</Label>
            <MultiCheckSelect
              options={categoryOptions}
              selected={selectedCategories}
              onChange={(next) => { setSelectedCategories(next); setSelectedSubCategory(''); setSelectedItem(''); }}
              allLabel="All Categories"
              noun="categories"
              searchPlaceholder="Search category..."
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Sub Category</Label>
            <Select value={selectedSubCategory} onChange={e => { setSelectedSubCategory(e.target.value); setSelectedItem(''); }}>
              <option value="">All Sub Categories</option>
              {filteredSubCategories.map(sc => (
                <option key={sc.id} value={sc.id}>
                  {sc.subCategoryCode ? `${sc.subCategoryCode} - ` : ''}{sc.subCategoryName}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Item</Label>
            <ItemSelect
              items={filteredItemsDropdown}
              selectedItemId={selectedItem}
              onChange={setSelectedItem}
              allOption={{ value: '', label: 'All Items' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={selectedCustomer}
              onChange={setSelectedCustomer}
              allOption={{ value: '', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
              <option value="All">All Statuses</option>
              <option value="Posted">Posted</option>
              <option value="Paid">Paid</option>
              <option value="Partial">Partial</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <ActionButton onClick={handleExport} disabled={sortedData.length === 0}>
            <FiDownload /> Export CSV
          </ActionButton>
          <ActionButton onClick={handlePrint} disabled={sortedData.length === 0}>
            <FiPrinter /> Print
          </ActionButton>
          <ActionButton $primary onClick={loadTransactions} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </ActionButton>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <CardsGrid>
          <SummaryCard>
            <CardIcon $color={colors.primary.main}><FiHash /></CardIcon>
            <CardInfo>
              <CardLabel>Total Items Sold</CardLabel>
              <CardValue>{formatNumber(summary.totalItemsSold)}</CardValue>
              <CardSub>Unique items</CardSub>
            </CardInfo>
          </SummaryCard>

          <SummaryCard>
            <CardIcon $color={colors.chart.accent2}><FiBox /></CardIcon>
            <CardInfo>
              <CardLabel>Total Qty Sold</CardLabel>
              <CardValue>{formatNumber(summary.totalQtySold)}</CardValue>
              <CardSub>Units sold</CardSub>
            </CardInfo>
          </SummaryCard>

          <SummaryCard>
            <CardIcon $color="#28A745"><FiDollarSign /></CardIcon>
            <CardInfo>
              <CardLabel>Total Sale Amount</CardLabel>
              <CardValue>{formatCurrency(summary.totalSaleAmount)}</CardValue>
              <CardSub>Net of returns</CardSub>
            </CardInfo>
          </SummaryCard>

          <SummaryCard>
            <CardIcon $color={colors.chart.accent3}><FiTrendingUp /></CardIcon>
            <CardInfo>
              <CardLabel>Avg Unit Price</CardLabel>
              <CardValue>{formatCurrency(summary.avgUnitPrice)}</CardValue>
              <CardSub>Weighted average</CardSub>
            </CardInfo>
          </SummaryCard>
        </CardsGrid>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableHeader>
          <TableHeaderTitle>
            Item Sale Details
            {hasSearched && <TableCount>{sortedData.length} item{sortedData.length !== 1 ? 's' : ''}</TableCount>}
          </TableHeaderTitle>
        </TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <EmptyText>Loading item sale data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiBarChart2 /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view item sale analysis</EmptyText>
              <EmptySubText>Choose a financial year and date range to analyze item-level sales</EmptySubText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiBox /></EmptyIcon>
              <EmptyText>No item sale data found for the selected criteria</EmptyText>
              <EmptySubText>Try adjusting your filters or selecting a different financial year</EmptySubText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th $sortable onClick={() => handleSort('itemCode')}>
                    Item Code {renderSortIcon('itemCode')}
                  </Th>
                  <Th $sortable onClick={() => handleSort('itemName')}>
                    Item Name {renderSortIcon('itemName')}
                  </Th>
                  <Th $sortable onClick={() => handleSort('categoryName')}>
                    Category {renderSortIcon('categoryName')}
                  </Th>
                  <Th $sortable onClick={() => handleSort('unitOfMeasure')}>
                    Unit {renderSortIcon('unitOfMeasure')}
                  </Th>
                  <Th $sortable $right onClick={() => handleSort('qtySold')}>
                    Qty Sold {renderSortIcon('qtySold')}
                  </Th>
                  <Th $sortable $right onClick={() => handleSort('qtyReturned')}>
                    Qty Returned {renderSortIcon('qtyReturned')}
                  </Th>
                  <Th $sortable $right onClick={() => handleSort('netQty')}>
                    Net Qty {renderSortIcon('netQty')}
                  </Th>
                  <Th $sortable $right onClick={() => handleSort('avgPrice')}>
                    Avg Price {renderSortIcon('avgPrice')}
                  </Th>
                  <Th $sortable $right onClick={() => handleSort('totalAmount')}>
                    Total Amount {renderSortIcon('totalAmount')}
                  </Th>
                  <Th $sortable $right onClick={() => handleSort('percentOfTotal')}>
                    % of Total {renderSortIcon('percentOfTotal')}
                  </Th>
                </tr>
              </Thead>
              <Tbody>
                {sortedData.map((item, idx) => (
                  <Tr key={item.itemId || idx}>
                    <Td>
                      <ItemCode>{item.itemCode}</ItemCode>
                    </Td>
                    <Td title={item.itemName}>
                      <ItemLink onClick={() => navigate('/reports/inventory-ledger', { state: { itemId: item.itemId, itemName: item.itemName } })}>
                        {item.itemName}
                      </ItemLink>
                    </Td>
                    <Td>{item.categoryName || '-'}</Td>
                    <Td>{item.unitOfMeasure}</Td>
                    <AmountTd $right>{formatNumber(item.qtySold)}</AmountTd>
                    <AmountTd $right $negative={item.qtyReturned > 0}>
                      {item.qtyReturned > 0 ? formatNumber(item.qtyReturned) : '-'}
                    </AmountTd>
                    <AmountTd $right $highlight>{formatNumber(item.netQty)}</AmountTd>
                    <AmountTd $right>{formatAmountLocal(item.avgPrice)}</AmountTd>
                    <AmountTd $right $highlight>{formatAmountLocal(item.totalAmount)}</AmountTd>
                    <AmountTd $right>
                      <PercentBar>
                        <PercentBarFill style={{ width: `${Math.min(item.percentOfTotal, 100)}%` }} />
                        <PercentText>{item.percentOfTotal.toFixed(1)}%</PercentText>
                      </PercentBar>
                    </AmountTd>
                  </Tr>
                ))}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                    TOTAL ({sortedData.length} items)
                  </Td>
                  <AmountTd $right>{formatNumber(tableTotals.qtySold)}</AmountTd>
                  <AmountTd $right>{formatNumber(tableTotals.qtyReturned)}</AmountTd>
                  <AmountTd $right $highlight>{formatNumber(tableTotals.netQty)}</AmountTd>
                  <AmountTd $right></AmountTd>
                  <AmountTd $right $highlight>{formatAmountLocal(tableTotals.totalAmount)}</AmountTd>
                  <AmountTd $right>100%</AmountTd>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasSearched && !loading && sortedData.length > 0 && (
        <ChartsGrid>
          {/* Bar Chart: Top 15 Items by Sale Amount */}
          <ChartCard>
            <ChartTitle>Top 15 Items by Sale Amount</ChartTitle>
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={barChartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    width={140}
                  />
                  <Tooltip content={<BarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="amount" name="Sale Amount" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No item data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Pie Chart: Sales by Category */}
          <ChartCard>
            <ChartTitle>Sales by Category</ChartTitle>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={340}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name.length > 14 ? name.substring(0, 14) + '..' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {pieChartData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No category data available</ChartEmpty>
            )}
          </ChartCard>
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
//  Styled Components
// ══════════════════════════════════════════════════════════════

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;
  white-space: nowrap;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ── Summary Cards ──

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 600;
`;

const CardValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

// ── Charts ──

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipWrapper = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 10px 14px;
  font-size: 12px;
`;

const ChartTooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
    margin-left: auto;
  }
`;

const ChartTooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const TableHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.primary.main}08;
  border-top: 2px solid ${colors.primary.main};

  td {
    font-weight: 700 !important;
    font-size: 13px !important;
    padding: 12px 14px !important;
  }

  &:hover {
    background: ${colors.primary.main}08;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  text-align: right;
  color: ${props => {
    if (props.$negative) return colors.error.main;
    if (props.$highlight) return colors.text.primary;
    return colors.text.secondary;
  }};
  font-weight: ${props => props.$highlight ? 600 : 400};
`;

const ItemCode = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'SF Mono', 'Consolas', monospace;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
`;

const PercentBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 80px;
  height: 20px;
  background: ${colors.neutral[100]};
  border-radius: 3px;
  overflow: hidden;
`;

const PercentBarFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: ${colors.primary.main}25;
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const PercentText = styled.span`
  position: relative;
  z-index: 1;
  width: 100%;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

// ── Empty / Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  margin: 0 auto 16px auto;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default ItemSaleAnalysis;
